import React, { useState } from 'react';
import { PricingCard } from '../components/PricingCard';
import {
  ManagedServiceQuoteProvider,
  QuoteRequestPayload,
  QuoteSubmissionResult,
} from '../engine/adapters/quote/quoteProvider';
import {
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Smartphone,
  QrCode,
  PhoneCall,
  Send,
  Compass,
  Languages,
  TrendingUp,
  BarChart3,
  Users,
  Star,
  Zap,
  Building2,
  Share2,
  Image as ImageIcon,
  Box,
  HelpCircle,
} from 'lucide-react';

interface LandingPageProps {
  onBackToProfile?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onBackToProfile }) => {
  const [lang, setLang] = useState<'ar' | 'en'>('ar');
  const isAr = lang === 'ar';

  // Quote Form State
  const [selectedPackage, setSelectedPackage] = useState<'starter' | 'business' | 'premium'>('business');
  const [formData, setFormData] = useState<Partial<QuoteRequestPayload>>({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    country: isAr ? 'سلطنة عمان' : 'Oman',
    businessDescription: '',
    preferredLanguage: 'ar',
    selectedPackage: 'business',
    existingWebsite: '',
    existingDomain: '',
    desiredSubdomain: '',
    socialLinks: '',
    virtualTourInterest: false,
    aiAssistantInterest: false,
    notes: '',
  });

  const handleSelectPackage = (pkg: 'starter' | 'business' | 'premium') => {
    setSelectedPackage(pkg);
    setFormData((prev) => ({ ...prev, selectedPackage: pkg }));
    scrollToQuote();
  };

  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<QuoteSubmissionResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const quoteProvider = new ManagedServiceQuoteProvider();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (
      !formData.companyName?.trim() ||
      !formData.contactPerson?.trim() ||
      !formData.phone?.trim() ||
      !formData.email?.trim() ||
      !formData.country?.trim() ||
      !formData.businessDescription?.trim()
    ) {
      setFormError(
        isAr
          ? 'يرجى ملء جميع الحقول الإلزامية المطلوبة (*).'
          : 'Please complete all required fields (*).'
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload: QuoteRequestPayload = {
        companyName: formData.companyName.trim(),
        contactPerson: formData.contactPerson.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        country: formData.country.trim(),
        businessDescription: formData.businessDescription.trim(),
        preferredLanguage: formData.preferredLanguage || lang,
        selectedPackage: formData.selectedPackage || selectedPackage,
        existingWebsite: formData.existingWebsite?.trim() || undefined,
        existingDomain: formData.existingDomain?.trim() || undefined,
        desiredSubdomain: formData.desiredSubdomain?.trim() || undefined,
        socialLinks: formData.socialLinks?.trim() || undefined,
        virtualTourInterest: Boolean(formData.virtualTourInterest),
        aiAssistantInterest: Boolean(formData.aiAssistantInterest),
        notes: formData.notes?.trim() || undefined,
        submittedAt: new Date().toISOString(),
      };

      const result = await quoteProvider.submitQuoteRequest(payload);
      setSubmissionResult(result);
    } catch {
      setFormError(
        isAr
          ? 'حدث خطأ أثناء معالجة الطلب، يرجى المحاولة مرة أخرى.'
          : 'An error occurred while processing your request. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToQuote = () => {
    const el = document.getElementById('quote-section');
    if (el) {
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className="min-h-screen w-full bg-[#07101F] text-slate-100 font-sans selection:bg-[#00C8FF] selection:text-white overflow-x-hidden"
    >
      {/* ── Ambient Background Atmosphere (Space/Depth) ─────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" aria-hidden="true">
        {/* Large cyan halo — top-left */}
        <div className="absolute -top-40 -left-40 w-[550px] h-[550px] rounded-full bg-[#00C8FF]/10 blur-[140px]" />
        {/* Violet halo — top-right */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#7857FF]/12 blur-[130px]" />
        {/* Restrained pink glow — middle left */}
        <div className="absolute top-[40%] -left-36 w-[420px] h-[420px] rounded-full bg-[#EC4899]/6 blur-[140px]" />
        {/* Warm orange/gold glow — bottom right */}
        <div className="absolute bottom-10 right-0 w-[500px] h-[400px] rounded-full bg-[#FF8A1F]/6 blur-[130px]" />
        {/* Subtle light streak lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00C8FF]/15 to-transparent" />
        <div className="absolute top-[48%] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#7857FF]/10 to-transparent" />
      </div>

      {/* ── Header / Top Navigation Bar ──────────────────────── */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-[#07101F]/85 border-b border-white/5 px-4 sm:px-8 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3 animate-fade-in">
          <div className="w-10 h-10 rounded-xl bg-slate-900/90 border border-sky-400/30 flex items-center justify-center shadow-[0_0_20px_rgba(14,165,233,0.22)] overflow-hidden">
            <img
              src="/assets/jetown-logo.png"
              alt="JetOwn"
              className="w-7 h-7 object-contain select-none pointer-events-none"
              loading="eager"
            />
          </div>
          <div>
            <span className="font-extrabold text-sm sm:text-base tracking-tight text-white leading-none">
              Smart Digital Profile
            </span>
            <span className="block text-[10px] text-sky-400 font-medium mt-0.5">
              {isAr ? 'منصة الهوية الرقمية من JetOwn' : 'by JetOwn Platform'}
            </span>
          </div>
        </div>

        {/* Center Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 text-xs font-semibold text-slate-300">
          <button
            type="button"
            onClick={() => scrollToSection('what-is-it')}
            className="hover:text-sky-300 transition-colors cursor-pointer"
          >
            {isAr ? 'المزايا' : 'Features'}
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('pricing')}
            className="hover:text-sky-300 transition-colors cursor-pointer"
          >
            {isAr ? 'الباقات' : 'Pricing'}
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('examples')}
            className="hover:text-sky-300 transition-colors cursor-pointer"
          >
            {isAr ? 'نماذج حقيقية' : 'Live Examples'}
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('how-it-works')}
            className="hover:text-sky-300 transition-colors cursor-pointer"
          >
            {isAr ? 'كيف تعمل' : 'How It Works'}
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('faq')}
            className="hover:text-sky-300 transition-colors cursor-pointer"
          >
            {isAr ? 'الأسئلة الشائعة' : 'FAQ'}
          </button>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language Toggle */}
          <button
            type="button"
            onClick={() => setLang(isAr ? 'en' : 'ar')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 hover:border-sky-500/40 text-xs font-semibold text-slate-300 hover:text-white transition-all cursor-pointer"
          >
            <Languages className="w-3.5 h-3.5 text-sky-400" />
            <span>{isAr ? 'English' : 'العربية'}</span>
          </button>

          {/* Quick Order CTA */}
          <button
            type="button"
            onClick={scrollToQuote}
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-sky-500 to-violet-600 hover:from-sky-400 hover:to-violet-500 text-white text-xs font-bold shadow-[0_0_15px_rgba(14,165,233,0.3)] transition-all cursor-pointer"
          >
            <span>{isAr ? 'اطلب بروفايلك' : 'Get Profile'}</span>
          </button>

          {/* Quick Return to Profile */}
          {onBackToProfile && (
            <button
              type="button"
              onClick={onBackToProfile}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 hover:border-sky-400/50 text-xs font-bold text-sky-300 transition-all cursor-pointer"
            >
              <span>{isAr ? 'عرض البروفايل' : 'View Profile'}</span>
            </button>
          )}
        </div>
      </header>

      {/* ── Main Content Flow ────────────────────────────────── */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-20 sm:space-y-28">

        {/* ── A. HERO SECTION (Split Desktop Layout) ───────────── */}
        <section className="relative pt-2 sm:pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Content Column */}
            <div className="lg:col-span-7 text-center lg:text-start flex flex-col items-center lg:items-start animate-fade-in">
              {/* Badge Pill */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs font-bold mb-5 shadow-[0_0_15px_rgba(14,165,233,0.15)]">
                <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                <span>
                  {isAr ? 'الجيل الجديد من بطاقات الأعمال والهويات المؤسسية' : 'Next-Generation Digital Business Identity'}
                </span>
              </div>

              {/* H1 Heading */}
              <h1 className="text-3xl sm:text-5xl md:text-5xl lg:text-[3.25rem] font-black text-white tracking-tight leading-tight sm:leading-tight mb-5">
                Smart Digital Profile{' '}
                <span className="block text-xl sm:text-3xl lg:text-[2.35rem] mt-2 text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-sky-300 to-violet-400 font-extrabold leading-tight">
                  {isAr ? 'بروفايل رقمي كامل لعرض أعمالك والتواصل مع عملائك' : 'Complete Digital Profile to Showcase Your Business'}
                </span>
              </h1>

              {/* Explanatory Lead Copy */}
              <p className="max-w-2xl text-sm sm:text-base md:text-lg text-slate-300/90 leading-relaxed mb-8">
                {isAr
                  ? 'هوية رقمية تفاعلية متكاملة ومتعددة اللغات للشركات والمدراء التنفيذيين وممثلي المبيعات، تجمع كافة وسائل الاتصال، الخدمات، المعارض الافتراضية، ورمز QR المعتمد على نطاقك الخاص.'
                  : 'A comprehensive, multilingual interactive digital identity for enterprises, executives, and sales teams. Consolidates all contact channels, services, virtual tours, and branded QR under your own domain.'}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto mb-8">
                <button
                  type="button"
                  onClick={scrollToQuote}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 via-blue-600 to-violet-600 hover:from-sky-400 hover:to-violet-500 text-white font-bold text-sm shadow-[0_10px_30px_rgba(14,165,233,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
                >
                  <span>{isAr ? 'اطلب بروفايلك الآن' : 'Request Your Profile'}</span>
                  <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                </button>

                <button
                  type="button"
                  onClick={() => scrollToSection('examples')}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/80 hover:border-sky-500/40 text-slate-200 hover:text-white text-sm font-bold transition-all cursor-pointer"
                >
                  <span>{isAr ? 'شاهد نموذجاً' : 'View Real Examples'}</span>
                  <ExternalLink className="w-4 h-4 text-sky-400" />
                </button>
              </div>

              {/* Trust signals below CTA */}
              <div className="pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-center lg:justify-start gap-4 text-xs text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>{isAr ? 'مناسب لجميع الأعمال' : 'For All Businesses'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                  <span>{isAr ? 'متجاوب مع جميع الأجهزة' : 'Cross-Device'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span>{isAr ? 'آمن وموثوق' : 'Secure & Reliable'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>{isAr ? 'سريع الانطلاق' : 'Fast Launch'}</span>
                </div>
              </div>
            </div>

            {/* Visual Column: Three Concentric Orbits, Moving Particles, Earth Element, Floating Logo */}
            <div className="lg:col-span-5 flex items-center justify-center relative select-none">
              <div className="relative flex items-center justify-center w-full max-w-[340px] sm:max-w-[430px] aspect-square">
                {/* ── A. Earth / Globe Visual in Lower-Right Background ──────── */}
                <div className="absolute -bottom-8 -right-6 sm:-bottom-12 sm:-right-10 w-52 h-52 sm:w-68 sm:h-68 rounded-full pointer-events-none -z-10 overflow-hidden opacity-70 sm:opacity-85">
                  <div className="absolute inset-0 rounded-full shadow-[inset_12px_12px_35px_rgba(0,200,255,0.40),inset_-15px_-15px_50px_rgba(5,11,22,0.95),0_-8px_30px_rgba(0,200,255,0.20)]" />
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    <defs>
                      <radialGradient id="earthGrad" cx="30%" cy="30%" r="70%">
                        <stop offset="0%" stopColor="#0E3263" />
                        <stop offset="45%" stopColor="#071B38" />
                        <stop offset="85%" stopColor="#040C1A" />
                        <stop offset="100%" stopColor="#02060E" />
                      </radialGradient>
                      <linearGradient id="atmoGlow" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00C8FF" stopOpacity="0.55" />
                        <stop offset="50%" stopColor="#7857FF" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <circle cx="100" cy="100" r="98" fill="url(#earthGrad)" />
                    <ellipse cx="100" cy="100" rx="95" ry="40" fill="none" stroke="rgba(0, 200, 255, 0.12)" strokeWidth="1" strokeDasharray="3 3" />
                    <ellipse cx="100" cy="100" rx="95" ry="70" fill="none" stroke="rgba(0, 200, 255, 0.10)" strokeWidth="1" />
                    <ellipse cx="100" cy="100" rx="45" ry="95" fill="none" stroke="rgba(120, 87, 255, 0.12)" strokeWidth="1" strokeDasharray="2 2" />
                    <ellipse cx="100" cy="100" rx="75" ry="95" fill="none" stroke="rgba(120, 87, 255, 0.08)" strokeWidth="1" />
                    <path d="M50,70 Q70,60 90,75 Q110,90 120,80 Q135,70 145,85 Q130,110 110,120 Q85,125 70,105 Z" fill="rgba(22, 139, 255, 0.12)" />
                    <path d="M100,125 Q115,115 130,130 Q140,145 125,160 Q105,155 95,140 Z" fill="rgba(0, 200, 255, 0.10)" />
                    <path d="M30,100 Q45,90 55,105 Q50,125 35,120 Z" fill="rgba(120, 87, 255, 0.09)" />
                    <circle cx="100" cy="100" r="97" fill="none" stroke="url(#atmoGlow)" strokeWidth="3" />
                  </svg>
                </div>

                {/* Ambient glow behind orbits */}
                <div className="absolute inset-0 rounded-full bg-sky-500/15 blur-[80px] pointer-events-none -z-10" />
                <div className="absolute inset-6 rounded-full bg-violet-600/12 blur-[65px] pointer-events-none -z-10" />

                {/* ── B. Orbit 1: Outer Orbit (Clockwise, 22s) ─── */}
                <div className="absolute w-[320px] h-[320px] sm:w-[410px] sm:h-[410px] rounded-full border border-sky-400/20 animate-orbit-outer pointer-events-none">
                  {/* 6 Outer orbit particles */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#00C8FF] shadow-[0_0_12px_#00C8FF]" />
                  <div className="absolute top-[13%] right-[13%] w-2 h-2 rounded-full bg-[#168BFF] shadow-[0_0_8px_#168BFF]" />
                  <div className="absolute bottom-[13%] right-[13%] w-2.5 h-2.5 rounded-full bg-[#7857FF] shadow-[0_0_10px_#7857FF]" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EC4899] shadow-[0_0_8px_#EC4899]" />
                  <div className="absolute bottom-[13%] left-[13%] w-3 h-3 rounded-full bg-[#00C8FF] shadow-[0_0_12px_#00C8FF]" />
                  <div className="absolute top-[13%] left-[13%] w-2 h-2 rounded-full bg-[#A855F7] shadow-[0_0_8px_#A855F7]" />
                </div>

                {/* ── C. Orbit 2: Middle Orbit (Counter-Clockwise, 16s) ─── */}
                <div className="absolute w-[240px] h-[240px] sm:w-[305px] sm:h-[305px] rounded-full border border-dashed border-violet-400/30 animate-orbit-middle pointer-events-none">
                  {/* 5 Middle orbit particles */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#7857FF] shadow-[0_0_10px_#7857FF]" />
                  <div className="absolute top-[22%] right-[5%] w-2 h-2 rounded-full bg-[#00C8FF] shadow-[0_0_8px_#00C8FF]" />
                  <div className="absolute bottom-[12%] right-[20%] w-2 h-2 rounded-full bg-[#EC4899] shadow-[0_0_8px_#EC4899]" />
                  <div className="absolute bottom-[12%] left-[20%] w-2.5 h-2.5 rounded-full bg-[#168BFF] shadow-[0_0_10px_#168BFF]" />
                  <div className="absolute top-[22%] left-[5%] w-2 h-2 rounded-full bg-[#A855F7] shadow-[0_0_8px_#A855F7]" />
                </div>

                {/* ── D. Orbit 3: Inner Orbit (Clockwise, 10s) ─── */}
                <div className="absolute w-[160px] h-[160px] sm:w-[205px] sm:h-[205px] rounded-full border border-sky-400/30 animate-orbit-inner pointer-events-none">
                  {/* 4 Inner orbit particles */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#00C8FF] shadow-[0_0_10px_#00C8FF]" />
                  <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EC4899] shadow-[0_0_8px_#EC4899]" />
                  <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#168BFF] shadow-[0_0_8px_#168BFF]" />
                  <div className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#7857FF] shadow-[0_0_10px_#7857FF]" />
                </div>

                {/* ── E. Center: Floating JetOwn Logo (NO SQUARE OR CARD CONTAINER) ── */}
                <div className="relative flex items-center justify-center pointer-events-none z-10 select-none">
                  {/* Subtle core ambient halo */}
                  <div className="absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-tr from-sky-400/25 via-violet-500/20 to-transparent blur-xl pointer-events-none" />
                  <img
                    src="/assets/jetown-logo.png"
                    alt="JetOwn Rocket"
                    className="w-24 h-24 sm:w-32 sm:h-32 object-contain select-none pointer-events-none drop-shadow-[0_8px_25px_rgba(0,200,255,0.45)] animate-subtle-float"
                    loading="eager"
                  />
                </div>

                {/* ── F. Floating Benefit Badges ───────────────────────── */}
                {/* Badge 1 — Top Start */}
                <div className="absolute top-1 sm:-top-3 start-1 sm:-start-6 z-20 px-3 py-1.5 rounded-2xl bg-[#0D1B31]/90 border border-sky-400/35 backdrop-blur-xl shadow-[0_8px_25px_rgba(0,200,255,0.25)] flex items-center gap-2 text-xs font-bold text-slate-100 animate-subtle-float">
                  <TrendingUp className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                  <span>{isAr ? 'نمو أكبر لعملك 📈' : 'Grow Your Business 📈'}</span>
                </div>

                {/* Badge 2 — Middle End */}
                <div className="absolute top-1/2 -end-1 sm:-end-8 -translate-y-1/2 z-20 px-3 py-1.5 rounded-2xl bg-[#0D1B31]/90 border border-violet-400/35 backdrop-blur-xl shadow-[0_8px_25px_rgba(120,87,255,0.25)] flex items-center gap-2 text-xs font-bold text-slate-100 animate-subtle-float-reverse">
                  <BarChart3 className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                  <span>{isAr ? 'عزز حضورك الرقمي 📊' : 'Strengthen Your Digital Presence 📊'}</span>
                </div>

                {/* Badge 3 — Bottom Start */}
                <div className="absolute bottom-1 sm:-bottom-3 start-2 sm:-start-6 z-20 px-3 py-1.5 rounded-2xl bg-[#0D1B31]/90 border border-emerald-400/35 backdrop-blur-xl shadow-[0_8px_25px_rgba(16,185,129,0.25)] flex items-center gap-2 text-xs font-bold text-slate-100 animate-subtle-float">
                  <Users className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span>{isAr ? 'تواصل أسهل مع عملائك 👥' : 'Connect with Customers Easily 👥'}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── B. WHAT IS IT? (8 Capability Cards matching Reference) ── */}
        <section id="what-is-it" className="rounded-3xl bg-gradient-to-b from-[#101A30]/70 to-[#0B1020]/80 border border-sky-500/20 p-6 sm:p-10 backdrop-blur-xl shadow-xl">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="section-accent-bar" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">
              {isAr ? 'ما هو الملف الرقمي الذكي؟' : 'What is the Smart Digital Profile?'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              {isAr
                ? 'هو منصة رقمية متطورة وسريعة الاستجابة تعمل على جميع الهواتف الذكية وأجهزة الحاسوب بدون الحاجة لتحميل أي تطبيق. يحل محل بطاقات العمل الورقية التقليدية ويتيح لك تحديث بياناتك وخدماتك والتواصل مع عملائك بلحظات.'
                : 'A high-speed, responsive digital identity platform that works seamlessly across all smartphones and desktops without requiring app installation. Replaces traditional paper cards while allowing instant contact and live content updates.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 text-start">
            {[
              {
                icon: Building2,
                titleAr: 'معلومات الشركة',
                titleEn: 'Company Identity',
                descAr: 'عرض هوية ونشاط شركتك وفريقك باحترافية وتوثيق معتمد.',
                descEn: 'Showcase your corporate identity and verified business presence.',
              },
              {
                icon: Share2,
                titleAr: 'روابط التواصل',
                titleEn: 'Social & Channels',
                descAr: 'تجميع كافة شبكات التواصل الاجتماعي وقنوات الاتصال في مكان واحد.',
                descEn: 'Consolidate all social channels and communication links in one hub.',
              },
              {
                icon: PhoneCall,
                titleAr: 'تواصل مباشر',
                titleEn: 'Direct Contact',
                descAr: 'اتصال هاتفي مباشر، محادثة واتساب فورية، ومراسلة بريدية بنقرة واحدة.',
                descEn: 'One-tap direct calling, WhatsApp chat, and official email communication.',
              },
              {
                icon: QrCode,
                titleAr: 'رمز QR مخصص',
                titleEn: 'Branded QR Code',
                descAr: 'رمز QR هندسي عالي الدقة بهوية وشعار شركتك جاهز للطباعة والمشاركة.',
                descEn: 'High-contrast ISO-standard QR code with your brand logo ready for print.',
              },
              {
                icon: ImageIcon,
                titleAr: 'معرض الصور والفيديو',
                titleEn: 'Media Showcase',
                descAr: 'استعراض خدماتك ومنتجاتك بصرياً بأعلى نقاوة وسرعة تحميل فائقة.',
                descEn: 'High-resolution media galleries to showcase your products and projects.',
              },
              {
                icon: Compass,
                titleAr: 'جولات ومنتجات 360°',
                titleEn: '360° Virtual Tours',
                descAr: 'معارض تفاعلية ثلاثية الأبعاد تسمح للعملاء بزيارة منشأتك افتراضياً.',
                descEn: 'Interactive 360° walkthroughs allowing clients to explore your showroom.',
              },
              {
                icon: Box,
                titleAr: 'منصات الميتافيرس',
                titleEn: 'Metaverse & VR',
                descAr: 'ربط مباشر مع بيئات Jet-Malls والواقع الافتراضي لتجربة مستقبلية.',
                descEn: 'Direct link to Jet-Malls VR and metaverse digital retail spaces.',
              },
              {
                icon: Send,
                titleAr: 'نموذج طلب واستفسار',
                titleEn: 'Lead & Inquiry Form',
                descAr: 'استقبال طلبات عروض الأسعار واستفسارات العملاء مباشرة إلى بريدك.',
                descEn: 'Capture customer leads and RFQs directly to your business channels.',
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="p-4 sm:p-5 rounded-2xl bg-[#16213B]/50 hover:bg-[#16213B]/80 border border-slate-700/50 hover:border-sky-500/40 flex flex-col gap-2.5 transition-all hover:scale-[1.02]"
                >
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xs sm:text-sm font-bold text-white">
                    {isAr ? item.titleAr : item.titleEn}
                  </h3>
                  <p className="text-[11px] text-slate-300/80 leading-relaxed">
                    {isAr ? item.descAr : item.descEn}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── C. KEY BENEFITS ─────────────────────────────────── */}
        <section id="benefits" className="space-y-6">
          <div className="text-center">
            <div className="section-accent-bar" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
              {isAr ? 'مزايا استثنائية لنمو أعمالك' : 'Core Business Benefits'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl mx-auto">
              {isAr
                ? 'صُممت كل ميزة لتعزيز الظهور الرقمي وسهولة الوصول لعملائك بأعلى معايير الأمان والسرعة.'
                : 'Every feature is engineered to elevate your digital presence and facilitate seamless customer engagement.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                titleAr: 'انطباع أول احترافي',
                titleEn: 'Professional First Impression',
                descAr: 'تصميم فاخر وعصري يعكس مكانة شركتك ويوثق هويتك البصرية بدقة وأناقة.',
                descEn: 'Modern luxury aesthetic that reflects enterprise credibility and authentic brand identity.',
              },
              {
                titleAr: 'تحديث المحتوى بدون إعادة طباعة',
                titleEn: 'Zero Re-Printing Costs',
                descAr: 'تغيير أرقام الهواتف أو الخدمات أو العناوين يتم سحابياً وبشكل فوري دون هدر تكاليف الطباعة.',
                descEn: 'Update phone numbers, personnel, or services instantly in the cloud with zero printing waste.',
              },
              {
                titleAr: 'توافق فوري مع واتساب ووسائل المشاركة',
                titleEn: 'Optimized Social Sharing',
                descAr: 'معاينة غنية ودقيقة عند إرسال الرابط عبر واتساب أو لينكد إن مع إظهار شعار شركتك بدقة.',
                descEn: 'Clean Open Graph cards showing your real brand logo and description on WhatsApp and social feeds.',
              },
              {
                titleAr: 'رمز QR عالي الدقة للطباعة والبطاقات',
                titleEn: 'Vector QR for Physical Print',
                descAr: 'توليد رموز QR هندسية مطابقة لمعايير ISO العالمية، صالحة للطباعة على بطاقات NFC والبروشورات.',
                descEn: 'ISO/IEC 18004 Level-H QR code generation ready for high-resolution physical cards and brochures.',
              },
              {
                titleAr: 'ربط المعارض والواقع الافتراضي',
                titleEn: '360° Virtual Tour Integration',
                descAr: 'إمكانية دمج تجربة جولة ثلاثية الأبعاد 360° لعرض مصنعك، صالة العرض، أو مشروعاتك لعملائك.',
                descEn: 'Seamless 360° virtual tour integration to let prospects walk through your facility or showroom.',
              },
              {
                titleAr: 'جاهزية أتمتة المستقبل (AI-Ready)',
                titleEn: 'AI & Automation Ready',
                descAr: 'بنية هندسية مهيأة للترقية لاحقاً وربط مساعد ذكي يجيب عن استفسارات العملاء على مدار الساعة.',
                descEn: 'Architected for optional future AI assistant and CRM integration as your business scales.',
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="p-5 sm:p-6 rounded-2xl bg-[#101A30]/60 border border-slate-800 hover:border-sky-500/40 transition-all hover:bg-[#16213B]/70 flex flex-col gap-2 hover:-translate-y-0.5"
              >
                <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400 font-bold text-xs mb-1">
                  {idx + 1}
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white">
                  {isAr ? card.titleAr : card.titleEn}
                </h3>
                <p className="text-xs text-slate-300/80 leading-relaxed">
                  {isAr ? card.descAr : card.descEn}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── C2. PRICING SECTION ─────────────────────────────── */}
        <section id="pricing" className="space-y-6">
          <div className="text-center">
            <div className="section-accent-bar" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
              {isAr ? 'خطط الاشتراك' : 'Subscription Plans'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300/80 max-w-xl mx-auto">
              {isAr
                ? 'ابدأ بحزمة تناسب مشروعك وقم بالترقية متى تطور عملك'
                : 'Start with the plan that fits your business — upgrade as you grow'}
            </p>
          </div>

          <div dir="ltr" className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-stretch pt-6 sm:pt-8">
            {/* Starter */}
            <PricingCard
              tier="starter"
              packageId="starter"
              tierName="Starter"
              title={isAr ? 'الباقة الأساسية' : 'Starter'}
              subtitle={isAr ? 'مناسبة للأفراد والمهنيين' : 'Suitable for individuals / professionals'}
              price="49 JOD"
              priceSuffix={isAr ? 'تأسيس' : 'setup'}
              renewalPrice="29 JOD / year"
              features={
                isAr
                  ? [
                      'ملف رقمي مخصص',
                      'اسم نطاق فرعي من JetOwn',
                      'روابط التواصل الأساسية',
                      'QR Code مخصص',
                    ]
                  : [
                      'Custom digital profile',
                      'JetOwn managed subdomain',
                      'Essential contact links',
                      'Branded QR Code',
                    ]
              }
              ctaLabel={isAr ? 'اطلب هذه الباقة' : 'Request This Package'}
              onCtaClick={() => handleSelectPackage('starter')}
              animationDelay={0}
            />

            {/* Business — Highlighted (Primary / Recommended Card) */}
            <PricingCard
              tier="business"
              packageId="business"
              tierName="Business"
              title={isAr ? 'باقة الأعمال' : 'Business'}
              subtitle={isAr ? 'مناسبة للشركات والأعمال' : 'Suitable for companies / businesses'}
              badge={isAr ? 'الأكثر طلبًا' : 'Recommended'}
              highlight
              price="99 JOD"
              priceSuffix={isAr ? 'تأسيس' : 'setup'}
              renewalPrice="49 JOD / year"
              features={
                isAr
                  ? [
                      'كل مزايا الباقة الأساسية',
                      'تصميم وهوية مخصصة للعلامة التجارية',
                      'ربط نطاق العميل',
                      'خدمات ومنتجات متعددة',
                      'خرائط الموقع + واتساب + وسائل التواصل',
                      'تكامل الجولة الافتراضية عند الطلب',
                    ]
                  : [
                      'Everything in Starter',
                      'Custom business branding',
                      'Client domain connection',
                      'Multiple services/products',
                      'Maps + WhatsApp + social integrations',
                      'Virtual Tour integration when requested',
                    ]
              }
              ctaLabel={isAr ? 'اطلب هذه الباقة' : 'Request This Package'}
              onCtaClick={() => handleSelectPackage('business')}
              animationDelay={100}
            />

            {/* Premium */}
            <PricingCard
              tier="premium"
              packageId="premium"
              tierName="Premium"
              title={isAr ? 'الباقة المتقدمة' : 'Premium'}
              subtitle={isAr ? 'للشركات المتقدمة ومتطلبات الهوية الفاخرة' : 'Suitable for advanced companies / premium identity requirements'}
              price="199 JOD"
              priceSuffix={isAr ? 'تأسيس' : 'setup'}
              renewalPrice="79 JOD / year"
              features={
                isAr
                  ? [
                      'كل مزايا Business',
                      'خصائص متقدمة للتصميم والهوية',
                      'دعم متعدد اللغات',
                      'محتوى متقدم ومواد تفاعلية',
                      'جاهزية تكامل AI Assistant عند الإطلاق',
                      'دعم مخصص ذو أولوية',
                    ]
                  : [
                      'Everything in Business',
                      'Advanced branding and identity options',
                      'Multi-language support',
                      'Advanced content / interactive media',
                      'AI Assistant integration readiness',
                      'Priority custom support',
                    ]
              }
              ctaLabel={isAr ? 'اطلب هذه الباقة' : 'Request This Package'}
              onCtaClick={() => handleSelectPackage('premium')}
              animationDelay={200}
            />
          </div>
        </section>

        {/* ── D. DOMAIN OPTIONS ──────────────────────────────── */}
        <section id="domains" className="rounded-3xl bg-gradient-to-br from-[#101A30]/90 via-[#0B1020]/80 to-[#16213B]/60 border border-sky-500/20 p-6 sm:p-8 shadow-lg">
          <div className="text-center mb-6">
            <div className="section-accent-bar" />
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">
              {isAr ? 'خيارات النطاق والعنوان الرقمي' : 'Domain & Addressing Options'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto">
              {isAr
                ? 'اختر الطريقة الأنسب لهويتك وعلامتك التجارية'
                : 'Choose the best addressing structure for your brand'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-[#0B1020]/90 border border-slate-700/60 hover:border-emerald-500/40 transition-all flex flex-col gap-2">
              <span className="text-xs font-semibold text-emerald-400">
                {isAr ? 'الخيار الأول (الأكثر تميزاً)' : 'Option 1 (Custom Domain)'}
              </span>
              <h3 className="text-sm sm:text-base font-bold text-white">
                {isAr ? 'نطاق فرعي مخصص لشركتك' : 'Your Dedicated Subdomain'}
              </h3>
              <div className="font-mono text-xs text-sky-300 bg-slate-950/80 p-2.5 rounded-xl border border-sky-500/20 dir-ltr text-start">
                https://profile.yourcompany.com
              </div>
              <p className="text-xs text-slate-300/80 leading-relaxed mt-1">
                {isAr
                  ? 'يتم ربط البروفايل مباشرة على نطاق موقعك الحالي عبر إعدادات DNS بسيطة، مما يعزز الثقة بهويتك.'
                  : 'Directly linked to your existing company domain via standard DNS CNAME record, enhancing brand trust.'}
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-[#0B1020]/90 border border-slate-700/60 hover:border-sky-500/40 transition-all flex flex-col gap-2">
              <span className="text-xs font-semibold text-sky-400">
                {isAr ? 'الخيار الثاني (الأسرع انطلاقاً)' : 'Option 2 (Managed Address)'}
              </span>
              <h3 className="text-sm sm:text-base font-bold text-white">
                {isAr ? 'عنوان سحابي مُدار من JetOwn' : 'Managed JetOwn Profile Address'}
              </h3>
              <div className="font-mono text-xs text-sky-300 bg-slate-950/80 p-2.5 rounded-xl border border-sky-500/20 dir-ltr text-start">
                https://profile.jetown.net/?client=yourbrand
              </div>
              <p className="text-xs text-slate-300/80 leading-relaxed mt-1">
                {isAr
                  ? 'نشر فوري دون الحاجة لامتلاك نطاق خاص، مع توفير رمز QR ورابط مختصر جاهز للمشاركة.'
                  : 'Fast setup without requiring your own domain, complete with branded QR code and shareable URL.'}
              </p>
            </div>
          </div>
        </section>

        {/* ── E. HOW IT WORKS ────────────────────────────────── */}
        <section id="how-it-works" className="space-y-6">
          <div className="text-center">
            <div className="section-accent-bar" />
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">
              {isAr ? 'كيف يتم تنفيذ وتجهيز بروفايلك؟' : 'How It Works (Current Service Flow)'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl mx-auto">
              {isAr
                ? 'خدمة مُدارة متكاملة يشرف عليها مهندسو ومصممو JetOwn لضمان دقة التنفيذ وأعلى جودة.'
                : 'A fully managed white-glove setup overseen by JetOwn engineers to guarantee premium execution.'}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
            {[
              {
                step: '01',
                titleAr: 'تقديم الطلب والبيانات',
                titleEn: 'Request & Brief',
                descAr: 'إرسال تفاصيل الشركة وروابط التواصل والخدمات عبر النموذج أدناه.',
                descEn: 'Submit your business details and desired contact links via the quote form.',
              },
              {
                step: '02',
                titleAr: 'المراجعة وعرض السعر',
                titleEn: 'Review & Quote',
                descAr: 'دراسة متطلبات الهوية وتزويدكم بعرض السعر والجدول الزمني للإعداد.',
                descEn: 'We evaluate your branding scope and provide a formal quote.',
              },
              {
                step: '03',
                titleAr: 'تجهيز وتصميم البروفايل',
                titleEn: 'Profile Engineering',
                descAr: 'معالجة الشعار الشفاف، بناء الرمز الذكي QR، وضبط اللغات والمحتوى.',
                descEn: 'Transparent logo normalization, vector QR engine setup, and bilingual copy build.',
              },
              {
                step: '04',
                titleAr: 'المعاينة والتدشين',
                titleEn: 'Review & Launch',
                descAr: 'مراجعة العميل للاعتماد النهائي، ثم ربط النطاق وإطلاق البروفايل فوراً.',
                descEn: 'Client interactive review, domain connection, and public production release.',
              },
            ].map((s, idx) => (
              <div
                key={idx}
                className="p-4 rounded-2xl bg-[#101A30]/60 border border-slate-800 hover:border-sky-500/30 flex flex-col items-center gap-1.5 transition-all hover:bg-[#16213B]/70"
              >
                <span className="text-xs font-black text-sky-400 px-2 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/20">
                  {s.step}
                </span>
                <h3 className="text-xs sm:text-sm font-bold text-white mt-1">
                  {isAr ? s.titleAr : s.titleEn}
                </h3>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  {isAr ? s.descAr : s.descEn}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── F. REAL PRODUCTION EXAMPLES ─────────────────────── */}
        <section id="examples" className="rounded-3xl bg-[#101A30]/50 border border-slate-800/80 p-6 sm:p-8 text-center space-y-6">
          <div>
            <div className="section-accent-bar" />
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">
              {isAr ? 'نماذج حقيقية عاملة في بيئة الإنتاج' : 'Live Production References'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl mx-auto">
              {isAr
                ? 'اطلع على بروفايلات حقيقية تم إطلاقها بنجاح لعملائنا في دول الخليج والشرق الأوسط.'
                : 'Inspect real live profiles deployed on the Smart Digital Profile platform.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {/* Example 1: JetOwn */}
            <a
              href="https://profile.jetown.net"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 sm:p-5 rounded-2xl bg-[#16213B]/60 hover:bg-[#16213B]/90 border border-sky-500/20 hover:border-sky-500/50 transition-all text-start group flex items-center justify-between"
            >
              <div>
                <div className="text-[11px] font-semibold text-sky-400 mb-0.5">
                  {isAr ? 'البروفايل المرجعي للتقنية' : 'Technology Reference Profile'}
                </div>
                <div className="text-sm font-bold text-white group-hover:text-sky-300 transition-colors">
                  JetOwn | البوابة الرائدة لتكنولوجيا المعلومات
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono dir-ltr">
                  profile.jetown.net
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-sky-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </a>

            {/* Example 2: Modern Golden Rocks */}
            <a
              href="https://profile.mgrmix.com"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 sm:p-5 rounded-2xl bg-[#16213B]/60 hover:bg-[#16213B]/90 border border-emerald-500/20 hover:border-emerald-500/50 transition-all text-start group flex items-center justify-between"
            >
              <div>
                <div className="text-[11px] font-semibold text-emerald-400 mb-0.5">
                  {isAr ? 'القطاع الصناعي والتجاري' : 'Industrial & Commercial Profile'}
                </div>
                <div className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">
                  الصخور الذهبية الحديثة ش.م.م (MGR)
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono dir-ltr">
                  profile.mgrmix.com
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-emerald-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </section>

        {/* ── F2. FREQUENTLY ASKED QUESTIONS (FAQ) ────────────── */}
        <section id="faq" className="space-y-6">
          <div className="text-center">
            <div className="section-accent-bar" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
              {isAr ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl mx-auto">
              {isAr
                ? 'إجابات واضحة على أكثر الأسئلة شيوعاً حول خدمة وتجهيز البروفايل الرقمي الذكي.'
                : 'Clear answers to common questions regarding our managed digital profile platform.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto text-start">
            {[
              {
                qAr: 'هل يحتاج العميل لتحميل تطبيق لفتح البروفايل؟',
                qEn: 'Does the customer need to download an app?',
                aAr: 'لا يحتاج العميل أو الزائر إلى تحميل أي تطبيق على الإطلاق. يفتح البروفايل مباشرة وبسرعة فائقة عبر متصفح أي هاتف ذكي بمجرد مسح رمز الـ QR أو النقر على الرابط.',
                aEn: 'No app is required. The profile opens instantly in any web browser on smartphones and desktops upon scanning the QR code or tapping the link.',
              },
              {
                qAr: 'هل يمكنني ربط البروفايل بنطاق موقعي الخاص؟',
                qEn: 'Can I connect my own domain/subdomain?',
                aAr: 'نعم بالتأكيد. نوفر خيار ربط نطاق فرعي مخصص لشركتك (مثل profile.yourcompany.com) أو استخدام عنوان سحابي مُدار مجاناً عبر منصة JetOwn.',
                aEn: 'Yes. You can link your dedicated subdomain (e.g. profile.yourcompany.com) via DNS or use a managed address under the JetOwn platform.',
              },
              {
                qAr: 'كيف يتم تحديث أرقام التواصل أو الخدمات لاحقاً؟',
                qEn: 'How are phone numbers or services updated later?',
                aAr: 'تتم كافة التحديثات سحابياً وبشكل فوري دون المساس بالروابط أو الحاجة لإعادة طباعة بطاقات العمل أو المواد الترويجية.',
                aEn: 'All updates are performed instantly in the cloud without modifying links or re-printing physical business cards.',
              },
              {
                qAr: 'هل يدعم البروفايل اللغتين العربية والإنجليزية؟',
                qEn: 'Does the profile support Arabic and English?',
                aAr: 'نعم، المنصة مبنية وفق معايير متعددة اللغات (Multilingual-First)، مع تبديل سلس بين العربية (RTL) والإنجليزية (LTR) بما يناسب كافة عملائك.',
                aEn: 'Yes, the core architecture is inherently multilingual, supporting seamless bidirectional switching between Arabic (RTL) and English (LTR).',
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-[#101A30]/60 border border-slate-800 hover:border-sky-500/30 transition-all hover:bg-[#16213B]/70 flex flex-col gap-2"
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-6 h-6 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0 mt-0.5">
                    <HelpCircle className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-xs sm:text-sm font-bold text-white leading-snug">
                    {isAr ? faq.qAr : faq.qEn}
                  </h3>
                </div>
                <p className="text-xs text-slate-300/80 leading-relaxed ps-8.5">
                  {isAr ? faq.aAr : faq.aEn}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── G. REQUEST A QUOTE FORM ─────────────────────────── */}
        <section id="quote-section" className="rounded-3xl bg-[#101A30]/90 border border-sky-500/30 p-6 sm:p-10 shadow-2xl relative">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-bold mb-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>{isAr ? 'طلب عرض سعر وإعداد بروفايل' : 'Request Profile / Quote'}</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-extrabold text-white mb-2">
              {isAr ? 'اطلب ملفك الرقمي الذكي الآن' : 'Request Your Smart Digital Profile'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              {isAr
                ? 'املأ البيانات الأساسية أدناه وسيقوم فريق العمل بدراسة المتطلبات والتواصل معكم مباشرة بالخيارات المناسبة.'
                : 'Submit your basic business information below and our team will get in touch with formal options and setup guidance.'}
            </p>
          </div>

          {submissionResult ? (
            <div className="max-w-xl mx-auto p-6 sm:p-8 rounded-2xl bg-emerald-950/30 border border-emerald-500/40 text-center space-y-4">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto animate-bounce" />
              <h3 className="text-lg font-bold text-white">
                {isAr ? 'تم استلام طلبكم بنجاح' : 'Request Successfully Recorded'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
                {isAr ? submissionResult.message.ar : submissionResult.message.en}
              </p>
              <div className="inline-block px-4 py-1.5 rounded-xl bg-slate-900 border border-emerald-500/30 font-mono text-xs text-emerald-300 font-bold">
                REF: {submissionResult.referenceNumber}
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setSubmissionResult(null)}
                  className="text-xs font-semibold text-sky-400 hover:text-sky-300 underline cursor-pointer"
                >
                  {isAr ? 'إرسال طلب آخر' : 'Submit another request'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleQuoteSubmit} className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
              {formError && (
                <div className="p-3.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-medium">
                  {formError}
                </div>
              )}

              {/* Selected Package Field */}
              <div>
                <label htmlFor="selectedPackage" className="block text-xs font-bold text-slate-300 mb-1.5">
                  {isAr ? 'الباقة المختارة *' : 'Selected Package *'}
                </label>
                <select
                  id="selectedPackage"
                  name="selectedPackage"
                  value={selectedPackage}
                  onChange={(e) => {
                    const val = e.target.value as 'starter' | 'business' | 'premium';
                    setSelectedPackage(val);
                    setFormData((prev) => ({ ...prev, selectedPackage: val }));
                  }}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white transition-colors cursor-pointer"
                >
                  <option value="starter">
                    {isAr ? 'الباقة الأساسية (Starter) — 49 JOD setup / 29 JOD/year' : 'Starter — 49 JOD setup / 29 JOD/year'}
                  </option>
                  <option value="business">
                    {isAr ? 'باقة الأعمال (Business) — 99 JOD setup / 49 JOD/year (الأكثر طلبًا)' : 'Business — 99 JOD setup / 49 JOD/year (Recommended)'}
                  </option>
                  <option value="premium">
                    {isAr ? 'الباقة المتقدمة (Premium) — 199 JOD setup / 79 JOD/year' : 'Premium — 199 JOD setup / 79 JOD/year'}
                  </option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Company Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'اسم الشركة أو النشاط التجاري *' : 'Company / Business Name *'}
                  </label>
                  <input
                    type="text"
                    required
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder={isAr ? 'مثال: شركة الصخور الذهبية الحديثة' : 'e.g. Modern Golden Rocks LLC'}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 transition-colors"
                  />
                </div>

                {/* Contact Person */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'اسم مسؤول التواصل *' : 'Contact Person *'}
                  </label>
                  <input
                    type="text"
                    required
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder={isAr ? 'الاسم الثلاثي' : 'Full Name'}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Mobile / WhatsApp */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'رقم الهاتف / الواتساب *' : 'Phone / WhatsApp *'}
                  </label>
                  <input
                    type="tel"
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+968..."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 font-mono dir-ltr transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'البريد الإلكتروني الرسمي *' : 'Official Email *'}
                  </label>
                  <input
                    type="email"
                    required
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="info@company.com"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 font-mono dir-ltr transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Country */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'الدولة / المقر *' : 'Country / Location *'}
                  </label>
                  <input
                    type="text"
                    required
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder={isAr ? 'سلطنة عمان، الأردن، السعودية...' : 'Oman, Jordan, UAE, KSA...'}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 transition-colors"
                  />
                </div>

                {/* Preferred Language */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    {isAr ? 'لغة البروفايل الأساسية' : 'Primary Profile Language'}
                  </label>
                  <select
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white transition-colors"
                  >
                    <option value="ar">{isAr ? 'العربية والإنجليزية (ثنائي اللغة)' : 'Arabic & English (Bilingual)'}</option>
                    <option value="ar-only">{isAr ? 'العربية فقط' : 'Arabic Only'}</option>
                    <option value="en-only">{isAr ? 'الإنجليزية فقط' : 'English Only'}</option>
                  </select>
                </div>
              </div>

              {/* Business Description */}
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">
                  {isAr ? 'وصف موجز لطبيعة النشاط والخدمات *' : 'Brief Business / Activity Description *'}
                </label>
                <textarea
                  rows={3}
                  required
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  placeholder={
                    isAr
                      ? 'اذكر باختصار المنتجات أو الخدمات الرئيسية التي ترغب في تضمينها في البروفايل...'
                      : 'Briefly summarize your main products, services, or profile goals...'
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700/80 focus:border-sky-400 focus:outline-none text-xs text-white placeholder-slate-500 resize-none transition-colors"
                />
              </div>

              {/* Optional Section: Domain & Advanced Interests */}
              <div className="pt-2 border-t border-slate-800/80 space-y-3">
                <div className="text-xs font-bold text-slate-400">
                  {isAr ? 'خيارات إضافية (اختيارية):' : 'Optional Preferences:'}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">
                      {isAr ? 'الموقع الإلكتروني الحالي (إن وجد)' : 'Existing Website (if any)'}
                    </label>
                    <input
                      type="url"
                      name="existingWebsite"
                      value={formData.existingWebsite}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 rounded-xl bg-slate-800/50 border border-slate-700/60 text-xs text-white placeholder-slate-500 font-mono dir-ltr transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">
                      {isAr ? 'النطاق الفرعي المطلوب (اقتراح)' : 'Desired Subdomain Idea'}
                    </label>
                    <input
                      type="text"
                      name="desiredSubdomain"
                      value={formData.desiredSubdomain}
                      onChange={handleInputChange}
                      placeholder="e.g. profile.yourbrand.com"
                      className="w-full px-3 py-2 rounded-xl bg-slate-800/50 border border-slate-700/60 text-xs text-white placeholder-slate-500 font-mono dir-ltr transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                    <input
                      type="checkbox"
                      name="virtualTourInterest"
                      checked={formData.virtualTourInterest}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded bg-slate-800 border-slate-700 text-sky-500 focus:ring-sky-400"
                    />
                    <span>{isAr ? 'مهتم بإضافة جولة افتراضية 360°' : 'Interested in 360° Virtual Tour'}</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                    <input
                      type="checkbox"
                      name="aiAssistantInterest"
                      checked={formData.aiAssistantInterest}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded bg-slate-800 border-slate-700 text-sky-500 focus:ring-sky-400"
                    />
                    <span>{isAr ? 'مهتم بمساعد الذكاء الاصطناعي مستقبلاً' : 'Interested in future AI Assistant'}</span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-sky-500 via-blue-600 to-violet-600 hover:from-sky-400 hover:to-violet-500 text-white font-bold text-xs sm:text-sm shadow-[0_8px_30px_rgba(14,165,233,0.35)] hover:shadow-[0_10px_40px_rgba(14,165,233,0.50)] transition-all active:scale-[0.99] disabled:opacity-50 mt-4 cursor-pointer animate-gradient-shift"
              >
                <Send className="w-4 h-4" />
                <span>
                  {submitting
                    ? isAr
                      ? 'جارٍ تسجيل الطلب...'
                      : 'Submitting...'
                    : isAr
                    ? 'إرسال طلب البروفايل'
                    : 'Submit Profile Request'}
                </span>
              </button>
            </form>
          )}
        </section>

        {/* ── H. FINAL BOTTOM CONVERSION BANNER ──────────────── */}
        <section className="rounded-3xl bg-gradient-to-br from-[#101A30] via-[#16213B] to-[#101A30] border border-sky-500/30 p-8 sm:p-12 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-48 bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-2xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300 text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5 text-sky-400" />
              <span>{isAr ? 'ابدأ هويتك الرقمية الآن 🚀' : 'Launch Your Digital Identity 🚀'}</span>
            </div>
            <h2 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight">
              {isAr ? 'اطلب ملفك الرقمي الذكي الآن' : 'Ready to Transform Your Business Presence?'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-lg mx-auto leading-relaxed">
              {isAr
                ? 'انضم إلى الشركات الرائدة التي طورت حضورها الرقمي ببروفايل تفاعلي يعكس مكانتها ويسهل وصول العملاء لخدماتها.'
                : 'Join leading enterprises that upgraded their presence with an interactive profile reflecting true brand credibility.'}
            </p>
            <div className="pt-2">
              <button
                type="button"
                onClick={scrollToQuote}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 via-blue-600 to-violet-600 hover:from-sky-400 hover:to-violet-500 text-white font-bold text-sm shadow-[0_10px_30px_rgba(14,165,233,0.4)] transition-all hover:scale-[1.03] active:scale-[0.98] cursor-pointer"
              >
                <span>{isAr ? 'اطلب بروفايلك الآن' : 'Request Your Profile'}</span>
                <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ── Global Product Footer ────────────────────────────── */}
      <footer className="w-full border-t border-slate-800/80 py-10 px-4 sm:px-8 text-center space-y-3 mt-16 bg-[#070c18]/80 backdrop-blur-md">
        <div className="flex items-center justify-center gap-2 text-slate-400 font-semibold text-sm">
          <span>Smart Digital Profile Platform</span>
          <span className="text-slate-600">•</span>
          <span className="text-sky-400 font-bold">JetOwn</span>
          <ShieldCheck className="w-3.5 h-3.5 text-sky-400 inline" />
        </div>
        <p className="text-[11px] text-slate-500">
          © {new Date().getFullYear()} JetOwn Platform. All rights reserved. Enterprise Digital Identity Infrastructure.
        </p>
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
          <span>{isAr ? 'Powered by JetOwn — منصة الهوية الرقمية' : 'Powered by JetOwn Platform'}</span>
        </div>
      </footer>
    </div>
  );
};
